import React, { useState, useRef, useEffect } from 'react';
import { useChat } from '../hooks/useChat';
import ChatMessage from './ChatMessage';
import VoiceControls from './VoiceControls';
import LanguageToggle from './LanguageToggle';

const AIPsychologist = () => {
  const [language, setLanguage] = useState('es');
  const [inputMessage, setInputMessage] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const synthesisRef = useRef(null);

  const { 
    messages, 
    isLoading, 
    error, 
    sendMessage, 
    clearMessages, 
    initializeChat 
  } = useChat(language);

  const texts = {
    es: {
      title: "MindWise AI",
      subtitle: "Tu Psicólogo Virtual de Confianza",
      welcome: "¡Hola! Soy tu asistente psicológico virtual",
      welcomeDesc: "Estoy aquí para escucharte, apoyarte y ayudarte a reflexionar. Puedes hablarme por texto o por voz. Todo lo que compartamos será confidencial.",
      chatHeader: "Conversación Terapéutica",
      placeholder: "Escribe tu mensaje aquí...",
      listening: "Escuchando...",
      speaking: "Hablando...",
      thinking: "Pensando...",
      error: "Error de conexión",
      retry: "Reintentar",
      clear: "Nueva conversación"
    },
    en: {
      title: "MindWise AI", 
      subtitle: "Your Trusted Virtual Psychologist",
      welcome: "Hello! I'm your virtual psychological assistant",
      welcomeDesc: "I'm here to listen, support you, and help you reflect. You can talk to me via text or voice. Everything we share will be confidential.",
      chatHeader: "Therapeutic Conversation",
      placeholder: "Type your message here...",
      listening: "Listening...",
      speaking: "Speaking...",
      thinking: "Thinking...",
      error: "Connection error",
      retry: "Retry",
      clear: "New conversation"
    }
  };

  const currentTexts = texts[language];

  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = language === 'es' ? 'es-ES' : 'en-US';

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInputMessage(transcript);
        setIsListening(false);
      };

      recognitionRef.current.onerror = () => {
        setIsListening(false);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }

    if ('speechSynthesis' in window) {
      synthesisRef.current = window.speechSynthesis;
    }
  }, [language]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (messages.length === 0) {
      initializeChat();
    }
  }, [language, initializeChat, messages.length]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const messageToSend = inputMessage.trim();
    setInputMessage('');
    
    await sendMessage(messageToSend);
  };

  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    clearMessages();
    stopSpeaking();
  };

  const startListening = () => {
    if (recognitionRef.current && !isListening && !isLoading) {
      setIsListening(true);
      recognitionRef.current.start();
    }
  };

  const speakText = (text) => {
    if (synthesisRef.current && text) {
      setIsSpeaking(true);
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = language === 'es' ? 'es-ES' : 'en-US';
      utterance.rate = 0.9;
      utterance.pitch = 1;
      
      utterance.onend = () => {
        setIsSpeaking(false);
      };
      
      utterance.onerror = () => {
        setIsSpeaking(false);
      };
      
      synthesisRef.current.speak(utterance);
    }
  };

  const stopSpeaking = () => {
    if (synthesisRef.current) {
      synthesisRef.current.cancel();
      setIsSpeaking(false);
    }
  };

  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage && lastMessage.type === 'ai' && !isSpeaking) {
      speakText(lastMessage.content);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)', display: 'flex', flexDirection: 'column' }}>
      <header style={{ background: 'rgba(255, 255, 255, 0.1)', backdropFilter: 'blur(10px)', padding: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'white', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
          {currentTexts.title}
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {error && (
            <div style={{ background: 'rgba(239, 68, 68, 0.2)', color: 'white', padding: '0.5rem 1rem', borderRadius: '9999px', fontSize: '0.875rem' }}>
              {currentTexts.error}
            </div>
          )}
          <button
            onClick={clearMessages}
            style={{ background: 'rgba(255, 255, 255, 0.2)', color: 'white', padding: '0.5rem 1rem', borderRadius: '9999px', fontSize: '0.875rem', border: 'none', cursor: 'pointer', transition: 'all 0.3s' }}
          >
            {currentTexts.clear}
          </button>
          <LanguageToggle 
            language={language} 
            onLanguageChange={handleLanguageChange}
          />
        </div>
      </header>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', maxWidth: '1200px', width: '100%', margin: '0 auto', padding: '1.5rem', gap: '1.5rem' }}>
        {messages.length === 0 && (
          <div style={{ background: 'rgba(255, 255, 255, 0.95)', borderRadius: '24px', padding: '2rem', textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: '2rem', fontWeight: 'bold', background: 'linear-gradient(135deg, #667eea, #764ba2)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '1rem' }}>
              {currentTexts.welcome}
            </h2>
            <p style={{ fontSize: '1.125rem', color: '#4b5563', marginBottom: '1.5rem' }}>
              {currentTexts.welcomeDesc}
            </p>
          </div>
        )}

        <div style={{ background: 'rgba(255, 255, 255, 0.95)', borderRadius: '24px', flex: 1, display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
          <div style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)', color: 'white', padding: '1rem', textAlign: 'center', fontWeight: 'bold', fontSize: '1.125rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{currentTexts.chatHeader}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {isListening && (
                <span style={{ background: '#ef4444', padding: '0.25rem 0.5rem', borderRadius: '9999px', fontSize: '0.75rem', animation: 'pulse 2s infinite' }}>
                  {currentTexts.listening}
                </span>
              )}
              {isSpeaking && (
                <span style={{ background: '#10b981', padding: '0.25rem 0.5rem', borderRadius: '9999px', fontSize: '0.75rem', animation: 'pulse 2s infinite' }}>
                  {currentTexts.speaking}
                </span>
              )}
            </div>
          </div>

          <div style={{ flex: 1, padding: '1rem', overflowY: 'auto', maxHeight: '500px', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {messages.map((message) => (
              <ChatMessage 
                key={message.id} 
                message={message} 
                onSpeak={speakText}
                isSpeaking={isSpeaking}
              />
            ))}
            
            {isLoading && (
              <div style={{ maxWidth: '300px', padding: '1rem', background: '#f3f4f6', borderRadius: '16px', marginRight: 'auto', border: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{ display: 'flex', gap: '0.25rem' }}>
                    <div style={{ width: '8px', height: '8px', background: '#9ca3af', borderRadius: '50%', animation: 'bounce 1s infinite' }}></div>
                    <div style={{ width: '8px', height: '8px', background: '#9ca3af', borderRadius: '50%', animation: 'bounce 1s infinite 0.1s' }}></div>
                    <div style={{ width: '8px', height: '8px', background: '#9ca3af', borderRadius: '50%', animation: 'bounce 1s infinite 0.2s' }}></div>
                  </div>
                  <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>{currentTexts.thinking}</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div style={{ padding: '1rem', background: 'rgba(255, 255, 255, 0.8)', borderTop: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={currentTexts.placeholder}
              disabled={isLoading}
              style={{ flex: 1, padding: '0.75rem', border: '2px solid #d1d5db', borderRadius: '9999px', outline: 'none', transition: 'all 0.3s' }}
            />

            <VoiceControls
              isListening={isListening}
              isSpeaking={isSpeaking}
              isLoading={isLoading}
              onStartListening={startListening}
              onStopSpeaking={stopSpeaking}
            />

            <button
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || isLoading}
              style={{ width: '48px', height: '48px', background: isLoading || !inputMessage.trim() ? '#9ca3af' : 'linear-gradient(135deg, #667eea, #764ba2)', color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: isLoading || !inputMessage.trim() ? 'not-allowed' : 'pointer', transition: 'all 0.3s' }}
            >
              ✈️
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIPsychologist;